import { Dialog, Button, Grid, Typography, Link } from '@mui/material';
import { Box } from '@mui/system';
import { FC } from 'react';
import SvgIconStyle from '~/components/SvgIconStyle';
import DialogTitle from '~/components/lists/dialog-title';
import useAddToListDialog from '~/components/lists/add-to-list-dialog/useAddToListDialog';
import useCreateListDialog from '~/components/lists/create-new-list-dialog/useCreateListDialog';
import useListDialogs from '~/hooks/useListDialogs';
import useLocales from '@hooks/useLocales';
import useSync from '~/hooks/useSync';
import useSyncList from '~/hooks/useSyncList';
import useCurrentUser from '~/hooks/useCurrentUser';
import { SUPPORT_EMAIL } from '~/config';
import { styles } from './styles';
import { useSearchResults } from '~/sections/opportunities/search-results/useSearchResults';
import { RowsData } from '~/hooks/table/useSelectedRows';
import { OpportunityJSONAPI } from '~/api/resources/opportunity';

const ReviewCreditsDialog: FC = () => {
  const {
    reviewCreditsOpened,
    closeReviewCreditsDialog,
    selectedIds,
    selectedRows,
    selectedListId,
    syncType,
    createListParams,
  } = useListDialogs();
  const { onAddToList } = useAddToListDialog();
  const { onCreateList } = useCreateListDialog();
  const { onSyncSuccess } = useSearchResults();
  const { toggleSyncedMutate } = useSyncList({
    listId: String(selectedListId),
    isSynced: true,
    listType: 'OpportunityList',
    selectedIds: selectedIds,
    successToggleSyncCallback: () => {},
  });
  const { t } = useLocales();
  const { syncAgents } = useSync({ onSyncSuccess, listType: 'OpportunityList' });
  const { currentUser } = useCurrentUser();

  const submitSync = () => {
    if (syncType === 'toggle') {
      toggleSyncedMutate();
    } else if (syncType === 'list') {
      if (selectedListId === '' && createListParams) {
        onCreateList(createListParams);
      } else {
        onAddToList(selectedListId, selectedIds);
      }
    } else {
      syncAgents(selectedIds);
    }
    setTimeout(() => {
      closeReviewCreditsDialog(true);
    }, 300);
  };

  const backButtonOnClick = () => {
    closeReviewCreditsDialog(false);
  };

  const totalItems = selectedRows.filter((row) => {
    if ('attributes' in row) {
      return !row.attributes.synced_with_bpd_crm;
    }
    return !row.synced_with_crm;
  }).length;

  return (
    <Dialog onClose={() => closeReviewCreditsDialog(false)} open={reviewCreditsOpened}>
      <Grid direction="row" container rowSpacing="16px" sx={styles.dialogContainer}>
        <Grid item xs={12}>
          <DialogTitle
            onClose={() => closeReviewCreditsDialog(false)}
            title={t('opportunities:search_results.review_credits.title')}
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={styles.icon}>
            <SvgIconStyle src={'/icons/ic_bag.svg'} sx={styles.imageIcon} />
          </Box>
        </Grid>
        <Grid xs={10}>
          <Typography variant="subtitle1">{`${t(
            'opportunities:search_results.review_credits.total_credits'
          )} ${totalItems}`}</Typography>
        </Grid>
        <Grid xs={10.7}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('opportunities:search_results.review_credits.credits_per_item')}
          </Typography>
        </Grid>
        <Grid item xs={10.7} sx={styles.selectedItems}>
          <Typography variant="body2">
            {t('opportunities:search_results.review_credits.selected_items', {
              count: totalItems,
            })}
          </Typography>
        </Grid>
        <Grid direction="row" container rowSpacing="16px" ml="44px" mt="16px">
          <Grid item xs={10}>
            <Typography variant="body1">
              {t('opportunities:search_results.review_credits.credit_balance')}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="body1">{currentUser?.attributes.credits_amount}</Typography>
          </Grid>
          <Grid item xs={10} pb="16px" sx={styles.borderLine}>
            <Typography variant="body1">
              {t('opportunities:search_results.review_credits.credit_used')}
            </Typography>
          </Grid>
          <Grid item xs={0.7} pb="16px" sx={styles.borderLine}>
            <Typography variant="body1" sx={{ color: '#AA2553' }}>
              {/* TODO: add calculation based on default price for 1 sync */}-{totalItems}
            </Typography>
          </Grid>
          <Grid item xs={10} pb="16px">
            <Typography variant="subtitle1">
              {t('opportunities:search_results.review_credits.credit_remaining')}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1">
              {currentUser?.attributes.credits_amount
                ? currentUser?.attributes.credits_amount - totalItems
                : 0}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={11.2}>
          <Typography sx={styles.disclaimer}>
            {t('opportunities:search_results.review_credits.disclaimer')}{' '}
            <Link
              href={`mailto:${SUPPORT_EMAIL}`}
              sx={{ color: 'primary.main', textDecoration: 'none' }}
            >
              {SUPPORT_EMAIL}
            </Link>
            .
          </Typography>
        </Grid>
        <Grid direction="row" container rowSpacing="16px" ml="24px" mt="16px">
          <Grid item xs={7}>
            <Button
              startIcon={
                <SvgIconStyle
                  src="/icons/iconly/light/arrow_left_square.svg"
                  sx={styles.backButton.icon}
                />
              }
              size="medium"
              sx={styles.backButton.sx}
              onClick={backButtonOnClick}
              data-role="back-button"
            >
              {t('header.back_button')}
            </Button>
          </Grid>
          <Grid item xs={5} pb="16px">
            <Button variant="contained" color="primary" onClick={submitSync}>
              <Box sx={styles.icon}>
                <SvgIconStyle src={'/icons/ic_bag.svg'} sx={styles.imageIcon} />
              </Box>
              {t('opportunities:search_results.review_credits.confirm_sync')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default ReviewCreditsDialog;
